import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Plane as PlaneIcon, 
  Building as BuildingIcon, 
  Smile as SmileIcon, 
  Truck as TruckIcon, 
  Utensils as UtensilsIcon,
  Ship,
  Shield,
  Package,
  MapPin
} from "lucide-react";
import type { ComponentType } from "@shared/schema";

interface QuickAddItem {
  type: ComponentType;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const quickAddItems: QuickAddItem[] = [
  { 
    type: "flight", 
    label: "Flight", 
    icon: <PlaneIcon className="w-5 h-5" />,
    description: "Add flight booking details"
  },
  { 
    type: "hotel", 
    label: "Hotel", 
    icon: <BuildingIcon className="w-5 h-5" />,
    description: "Add accommodation details"
  },
  { 
    type: "activity", 
    label: "Activity", 
    icon: <SmileIcon className="w-5 h-5" />,
    description: "Add tours, excursions, or activities"
  },
  { 
    type: "transport", 
    label: "Transport", 
    icon: <TruckIcon className="w-5 h-5" />,
    description: "Add ground transportation"
  },
  { 
    type: "food", 
    label: "Meal", 
    icon: <UtensilsIcon className="w-5 h-5" />,
    description: "Add dining reservations"
  },
  { 
    type: "cruise", 
    label: "Cruise", 
    icon: <Ship className="w-5 h-5" />,
    description: "Add cruise booking details"
  },
  { 
    type: "travel-insurance", 
    label: "Travel Insurance", 
    icon: <Shield className="w-5 h-5" />,
    description: "Add travel insurance details"
  },
  { 
    type: "package", 
    label: "Package", 
    icon: <Package className="w-5 h-5" />,
    description: "Add vacation package details"
  }
];

interface QuickAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectType: (type: ComponentType) => void;
  dayIndex?: number;
}

export default function QuickAddModal({ 
  open, 
  onOpenChange, 
  onSelectType,
  dayIndex 
}: QuickAddModalProps) {
  const handleSelectType = (type: ComponentType) => {
    onSelectType(type);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Add Component{dayIndex !== undefined ? ` to Day ${dayIndex + 1}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-4">
          {quickAddItems.map((item) => (
            <Button
              key={item.type}
              variant="outline"
              onClick={() => handleSelectType(item.type)}
              className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-gray-50"
            >
              <div className="text-gray-600">
                {item.icon}
              </div>
              <div className="text-center">
                <div className="font-medium text-sm">{item.label}</div>
                <div className="text-xs text-gray-500 mt-1">{item.description}</div>
              </div>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
